// src/components/hall-prices/CreateAdminReservationModal.tsx
import React, { useEffect, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Field from "../ui/Field";
import {
    createAdminReservationRequest,
    addSeatsToAdminReservationRequest,
} from "../../services/api/adminReservations.api";
import { getUsersRequest, UserItem } from "../../services/api/users.api";

type Props = {
    isOpen: boolean;
    tourCityId: string | null;
    /** id уже выбранных в HallSeatPrice (id из таблицы hall_seat_prices) */
    selectedSeatPriceIds: string[];
    onClose: () => void;
    onCreated: () => void; // дернуть reload в родителе
};

const CreateAdminReservationModal: React.FC<Props> = ({
    isOpen,
    tourCityId,
    selectedSeatPriceIds,
    onClose,
    onCreated,
}) => {
    const [name, setName] = useState("");
    const [userId, setUserId] = useState<string>("");
    const [agents, setAgents] = useState<UserItem[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setName("");
        setUserId("");
        setError(null);
        setWarning(null);

        let cancelled = false;
        setAgentsLoading(true);

        (async () => {
            try {
                const response = await getUsersRequest({
                    is_agent: true,
                    role: "customer",
                    page: 1,
                    page_size: 100,
                    sort_by: "first_name",
                });
                if (cancelled) return;
                const raw: any = response as any;
                const list: UserItem[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.items)
                    ? raw.items
                    : Array.isArray(raw?.users)
                    ? raw.users
                    : Array.isArray(raw?.data)
                    ? raw.data
                    : [];
                setAgents(list);
            } catch (err) {
                if (!cancelled) {
                    setAgents([]);
                }
            } finally {
                if (!cancelled) setAgentsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    const handleSubmit = async () => {
        if (!tourCityId) {
            setError("Не указан город тура.");
            return;
        }
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Введите название брони.");
            return;
        }
        if (selectedSeatPriceIds.length === 0) {
            setError("Не выбрано ни одного места.");
            return;
        }

        setIsSaving(true);
        setError(null);
        setWarning(null);
        try {
            const created = await createAdminReservationRequest({
                name: trimmed,
                user_id: userId || null,
                tour_city_id: tourCityId,
            });

            const addResult = await addSeatsToAdminReservationRequest(
                created.id,
                selectedSeatPriceIds,
            );

            if (addResult.skipped && addResult.skipped.length > 0) {
                setWarning(
                    `Часть мест не добавлена (${addResult.skipped.length}). Возможно, они уже заняты.`,
                );
            }

            onCreated();
            // даём 0.5с показать предупреждение если оно есть
            if (!addResult.skipped || addResult.skipped.length === 0) {
                onClose();
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Не удалось создать бронь.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal-dialog" style={{ maxWidth: 480 }}>
                <div className="card notopacity">
                    <div className="card-body">
                        <h3 className="card-title">Административная бронь</h3>
                        <p className="card-text" style={{ marginBottom: 16 }}>
                            Выбрано мест: <strong>{selectedSeatPriceIds.length}</strong>
                        </p>

                        <Field label="Название" htmlFor="adm-res-name">
                            <Input
                                id="adm-res-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Например: Иван Иванов — 12.06"
                                autoFocus
                            />
                        </Field>

                        <Field label="Распространитель" htmlFor="adm-res-user">
                            <select
                                id="adm-res-user"
                                className="ui-select"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                disabled={agentsLoading}
                            >
                                <option value="">
                                    {agentsLoading
                                        ? "Загрузка..."
                                        : "— Без привязки —"}
                                </option>
                                {agents.map((agent) => {
                                    const fullName = [agent.first_name, agent.last_name]
                                        .filter(Boolean)
                                        .join(" ")
                                        .trim();
                                    const label = fullName
                                        ? `${fullName} (${agent.email})`
                                        : agent.email;
                                    return (
                                        <option key={agent.id} value={agent.id}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                        </Field>

                        {error && (
                            <p style={{ color: "#ef4444", marginTop: 8 }}>{error}</p>
                        )}
                        {warning && (
                            <p style={{ color: "#d97706", marginTop: 8 }}>{warning}</p>
                        )}

                        <div
                            className="editor-actions"
                            style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}
                        >
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Отмена
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSaving || !tourCityId || !name.trim()}
                            >
                                {isSaving ? "Сохранение..." : "Сохранить"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateAdminReservationModal;
